import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, BuildingOfficeIcon, UserIcon, EnvelopeIcon, PhoneIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';
import LoadingSpinner from '../LoadingSpinner';

interface MSP {
  id: number;
  company: string;
  logoUrl?: string;
  contactName: string;
  phone?: string;
  email: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  userCount?: number;
  activeInviteCodes?: number;
  totalInvitesUsed?: number;
}

interface MSPFormData {
  company: string;
  contactName: string;
  phone: string;
  email: string;
  password: string;
  logo?: File;
}

const MSPManagement: React.FC = () => {
  const [msps, setMSPs] = useState<MSP[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMSP, setEditingMSP] = useState<MSP | null>(null);
  const [formData, setFormData] = useState<MSPFormData>({
    company: '',
    contactName: '',
    phone: '',
    email: '',
    password: ''
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Helper function to get full URL for images
  const getImageUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000';
    return `${baseUrl}${url}`;
  };

  useEffect(() => {
    fetchMSPs();
  }, []);

  const fetchMSPs = async () => {
    try {
      setLoading(true);
      const response = await adminService.getMSPs({ search: searchTerm });
      // Filter out internal MSPs from display
      setMSPs(response.msps.filter(msp => !msp.isInternal));
    } catch (error) {
      console.error('Error fetching MSPs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('company', formData.company);
      formDataToSend.append('contactName', formData.contactName);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      if (!editingMSP || formData.password) {
        formDataToSend.append('password', formData.password);
      }
      if (formData.logo) {
        formDataToSend.append('logo', formData.logo);
      }

      if (editingMSP) {
        await adminService.updateMSP(editingMSP.id, formDataToSend);
      } else {
        await adminService.createMSP(formDataToSend);
      }

      setShowForm(false);
      setEditingMSP(null);
      setFormData({
        company: '',
        contactName: '',
        phone: '',
        email: '',
        password: ''
      });
      setLogoPreview(null);
      fetchMSPs();
    } catch (error) {
      console.error('Error saving MSP:', error);
    }
  };

  const handleEdit = (msp: MSP) => {
    setEditingMSP(msp);
    setFormData({
      company: msp.company,
      contactName: msp.contactName,
      phone: msp.phone || '',
      email: msp.email,
      password: ''
    });
    setLogoPreview(getImageUrl(msp.logoUrl) || null);
    setShowForm(true);
  };

  const handleDelete = async (mspId: number) => {
    try {
      await adminService.deleteMSP(mspId);
      setDeleteConfirm(null);
      fetchMSPs();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete MSP');
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const processLogoFile = (file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setFormData({ ...formData, logo: file });
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      processLogoFile(file);
    }
  };

  if (loading && msps.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">MSP Management</h2>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingMSP(null);
              setFormData({
                company: '',
                contactName: '',
                phone: '',
                email: '',
                password: ''
              });
              setLogoPreview(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add MSP
          </button>
        </div>
      </div>

      {showForm && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingMSP ? 'Edit MSP' : 'New MSP'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Enter company name"
                    className="pl-10 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    placeholder="Enter contact name"
                    className="pl-10 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="msp@example.com"
                    className="pl-10 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="pl-10 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password {editingMSP && <span className="text-gray-500 dark:text-gray-400 font-normal">(leave blank to keep current)</span>}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required={!editingMSP}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingMSP ? "Leave blank to keep current" : "Enter secure password"}
                    className="pl-10 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Logo</label>
                <div className="mt-1">
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 min-h-[160px] flex items-center justify-center ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    {!isDragging && logoPreview ? (
                      <div className="flex items-center space-x-4 w-full">
                        <div className="flex-shrink-0">
                          <img src={logoPreview} alt="Logo preview" className="h-16 w-16 object-contain rounded-lg" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Logo uploaded</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Drag and drop to replace</p>
                        </div>
                        <label className="cursor-pointer">
                          <span className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                            Change
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="sr-only"
                          />
                        </label>
                      </div>
                    ) : !isDragging ? (
                      <div className="text-center w-full">
                        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <label className="relative cursor-pointer">
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                              Upload a file
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="sr-only"
                            />
                          </label>
                          <span className="text-sm text-gray-500 dark:text-gray-400"> or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          PNG, JPG, GIF, WebP or SVG up to 5MB
                        </p>
                      </div>
                    ) : null}
                    
                    {isDragging && (
                      <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-lg pointer-events-none flex items-center justify-center">
                        <div className="text-center">
                          <PhotoIcon className="mx-auto h-12 w-12 text-blue-500 animate-bounce" />
                          <p className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400">Drop your image here</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingMSP(null);
                }}
                className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                {editingMSP ? 'Update' : 'Create'} MSP
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  MSP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Statistics
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {msps.map((msp) => (
                <tr key={msp.id} className={msp.isInternal ? 'bg-gray-50 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {msp.logoUrl ? (
                          <img className="h-10 w-10 rounded-full object-contain" src={getImageUrl(msp.logoUrl)} alt={msp.company} />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <BuildingOfficeIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {msp.company}
                          {msp.isInternal && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Internal
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{msp.contactName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{msp.email}</div>
                    {msp.phone && <div className="text-sm text-gray-500">{msp.phone}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {msp.userCount || 0} users
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {msp.activeInviteCodes || 0} active codes
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {msp.totalInvitesUsed || 0} invites used
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {!msp.isInternal && (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(msp)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit MSP"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        {deleteConfirm === msp.id ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDelete(msp.id)}
                              className="text-red-600 hover:text-red-900 text-xs"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-gray-600 hover:text-gray-900 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(msp.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete MSP"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MSPManagement;